import * as tg from "generic-type-guard";
import { cloneDeep } from "lodash";
import { SagaIterator } from "redux-saga";
import { call, put, SagaGenerator, select, takeLatest } from "typed-redux-saga";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { ApplicationModel, ModelActions, ModelSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import { ModelsRequest } from "@com.mgmtp.a12.dataservices/dataservices-access/lib/model";
import { ConnectorLocator, RestServerConnector } from "@com.mgmtp.a12.utils/utils-connector/lib/main";
import { WorkflowsConstants } from "@com.mgmtp.a12.workflows/workflows-core/lib/utils/constants";
import { Query } from "@com.mgmtp.a12.dataservices/dataservices-access";

import { fetchSagaFactory } from "../../../app/sagas/fetchSagaFactory";
import { setFVManifests } from "../../../app/slices/fachverfahren/reducers";
import { FachverfahrenManifest, RuntimeFachverfahrenManifest } from "../../../app/slices/fachverfahren/types";
import { Manifest } from "../../../app/slices/manifest/types";
import { setSDManifests } from "../../../app/slices/stammdaten/reducers";
import { RuntimeStammdatenManifest } from "../../../app/slices/stammdaten/types";
import { Descriptors } from "../../../config";
import { RESOURCE_KEYS } from "../../../localization";
import { addApplicationModel } from "../../../utilities/appModelHelpers";
import { DocumentReference } from "../../../utilities/documentReference";
import {
	isDocumentWithRoot,
	isFachverfahrenManifestDocument,
	isStammdatenManifest
} from "../../../utilities/loadAppmodelTypeguards";
import { putErrorNotification } from "../../../utilities/notificationUtils";
import { FACHVERFAHREN_MANIFEST, STAMMDATEN_MANIFEST } from "../../../utilities/variables/documentModelName";
import { CHANGELOG_DOCUMENT_MODEL } from "../../changelog/constants";
import { applyDeepLink } from "../../deepLinking/actions";
import { query } from "../../../utilities/rpcHelper";

import {
	APPMODEL_INITIAL_TASK_FORM_ANNOTATION,
	APPMODEL_PROCESS_ID_ANNOTATION,
	CROSSVERSION_DOC_MODEL_NAME,
	CROSSVERSION_OVERVIEW_MODEL_NAME,
	FV_MAIN_MODULE_NAME
} from "../constants";

import Direction = Query.Direction;
import NullHandling = Query.NullHandling;

type MutableApplicationModule = {
	-readonly [K in keyof ApplicationModel.Module]: ApplicationModel.Module[K];
};

interface AppModelWithManifest {
	model: ApplicationModel;
	manifest: Manifest;
}

const fixedAppmodelNames = [
	"dashboard-appmodel",
	"combinedList-appmodel",
	"stammdaten-appmodel",
	"ArchiveModal-appmodel"
];

export function* loadAppModels(): SagaIterator {
	yield* takeLatest([ModelActions.setModelGraph], loadAppModelWorker);
}

function* loadAppModelWorker(): SagaIterator {
	const fvManifests = yield* call(loadFachverfahrenManifests);
	const sdManifests = yield* call(loadStammdatenManifests);
	const manifests = [...fvManifests, ...sdManifests];

	const lastActivity = yield* select(ActivitySelectors.latestActivity());

	if (manifests.length === 0) {
		const userVisibility = yield* call(loadUserVisibility);

		yield* call(setFachverfahrenManifests, fvManifests);
		yield* call(setStammdatenManifests, sdManifests);
		// Öffne EmptyPortalPage mit zutreffendem Text (keine FV / keine Berechtigung), wenn keine Fachverfahren deployt sind
		yield* put(
			ActivityActions.create({
				activityDescriptor: {
					...Descriptors.emptyPortalPage,
					noAccess: userVisibility?.notVisibleFachverfahrenExist ? "true" : "false"
				},
				loadingState: "without",
				initiatingActivityId: lastActivity?.id
			})
		);

		return;
	}

	const responses = yield* call(() => Promise.all(manifests.map(it => loadAppModel(it.models.appModelId))));

	let appmodels: AppModelWithManifest[] = [];
	for (let i = 0; i < responses.length; i++) {
		const manifest = manifests[i];
		const appmodel = responses[i];
		appmodels.push({ model: appmodel, manifest: manifest });
	}
	appmodels = collateAppVersions(appmodels);

	const runtimeAppmodels: ApplicationModel[] = [];
	for (const fixedAppmodelName of fixedAppmodelNames) {
		const appmodel = yield* call(loadAppModel, fixedAppmodelName);
		runtimeAppmodels.push(appmodel);
	}
	yield* call(setFachverfahrenManifests, fvManifests);
	yield* call(setStammdatenManifests, sdManifests);

	const models: { model: ApplicationModel; fachverfahrenManifest?: FachverfahrenManifest }[] = [
		...runtimeAppmodels.map(runtimeAppmodel => ({ model: runtimeAppmodel })),
		...appmodels
	];

	yield* call(fillAppmodelAndDisplayInitialScene, models);
}

/**
 * Function to load the fachverfahren manifest data from the server
 * @returns the fachverfahren manifests
 */
function* loadFachverfahrenManifests(): SagaIterator<RuntimeFachverfahrenManifest[]> {
	const findManifestsQuery: Query.QueryRoot = manifestQuery(FACHVERFAHREN_MANIFEST);

	const response = yield* call(query, findManifestsQuery);

	return (response ?? []).flatMap(it => {
		const document = it.document;

		if (!isFachverfahrenManifestDocument(document)) {
			return [];
		}

		return {
			...document.root,
			moduleIds: [document.root.identification.id + "_Module"],
			documentReference: DocumentReference.fromString(it.docRef)
		};
	});
}

/**
 * Function to load the stammdaten manifest data from the server
 * @returns the stammdaten manifests
 */
function* loadStammdatenManifests(): SagaGenerator<RuntimeStammdatenManifest[]> {
	const findManifestsQuery: Query.QueryRoot = manifestQuery(STAMMDATEN_MANIFEST);

	const response = yield* call(query, findManifestsQuery);

	return (response ?? []).flatMap(it => {
		const document = it.document;

		if (!isDocumentWithRoot(document) || !isStammdatenManifest(document.root)) {
			return [];
		}

		return {
			...document.root,
			documentReference: DocumentReference.fromString(it.docRef)
		};
	});
}

const manifestQuery: (targetDocumentModel: string) => Query.QueryRoot = (targetDocumentModel: string) => ({
	projectionName: "document",
	targetDocumentModel,
	paging: {
		pageNumber: 0,
		pageSize: 1000
	},
	sort: [
		{
			field: "/root/identification/shortName",
			direction: Direction.ASC,
			ignoreCase: false,
			nullHandling: NullHandling.NULLS_LAST
		}
	]
});

const isUserVisibility = new tg.IsInterface()
	.withProperties({
		notVisibleFachverfahrenExist: tg.isBoolean
	})
	.get();

type UserVisibility = tg.GuardedType<typeof isUserVisibility>;

function* loadUserVisibility(): SagaGenerator<UserVisibility | undefined> {
	let result: UserVisibility | undefined = undefined;

	yield* call(
		fetchSagaFactory(
			{
				method: "GET",
				relativeUrl: "/user-visibility",
				customHeaders: [["Accepts", "application/json"]]
			},
			function* handleResponse(response) {
				const userVisibility = yield* call(response.json.bind(response));

				if (isUserVisibility(userVisibility)) {
					result = userVisibility;
				}
			},
			function* handleError(error) {
				yield* call(
					putErrorNotification,
					RESOURCE_KEYS.features.notification.error.unexpectedError,
					error,
					undefined,
					"loadUserVisibility"
				);
			}
		)
	);

	return result;
}

/**
 * Sets Fachverfahren manifests into the store
 * @param manifests
 */
function* setFachverfahrenManifests(manifests: RuntimeFachverfahrenManifest[]) {
	const setFVPayload: { [key: string]: RuntimeFachverfahrenManifest } = Object.fromEntries(
		manifests.map(e => [e.identification.id, e])
	);

	yield* put(setFVManifests(setFVPayload));
}

/**
 * Sets Stammdaten manifests into the store
 * @param manifests
 */
function* setStammdatenManifests(manifests: RuntimeStammdatenManifest[]) {
	const setSDPayload: { [key: string]: RuntimeStammdatenManifest } = Object.fromEntries(
		manifests.map(e => [e.identification.id, e])
	);

	yield* put(setSDManifests(setSDPayload));
}

/**
 * 	Adds the modules of all loaded appmodels to one parent appmodel that gets used by A12. Then opens the initial scene.
 * @param models the appmodels. Fachverfahren-specific ones have the fachverfahrenId field filled.
 */
function* fillAppmodelAndDisplayInitialScene(
	models: { model: ApplicationModel; fachverfahrenManifest?: FachverfahrenManifest }[]
): SagaGenerator<void> {
	const applicationModel = yield* select(ModelSelectors.applicationModel());
	const { model: combinedAppModel } = [{ model: applicationModel }, ...models].reduce((previous, current) => ({
		model: addApplicationModel(current.model, previous.model, current.fachverfahrenManifest)
	}));

	yield* put(ModelActions.setApplicationModel({ model: combinedAppModel }));
	yield* put(applyDeepLink());
}

async function loadAppModel(appModelID: string): Promise<ApplicationModel> {
	const serverConnector = ConnectorLocator.getInstance().getServerConnector() as RestServerConnector;
	const queryAppModelResponse = await serverConnector.fetchData(ModelsRequest.build({ id: appModelID }));
	return queryAppModelResponse.json();
}

/**
 * Combines multiple Application version appmodels into one crossversion appmodel per Application.
 * @param apps The raw list of available Application manifests and corresponding app models.
 * @returns A list of app models where app models of the same Application but in different versions have been combined.
 */
function collateAppVersions(apps: AppModelWithManifest[]): AppModelWithManifest[] {
	const appMap: { [key: string]: AppModelWithManifest[] } = apps.reduce(
		(result, app) => {
			const key = app.manifest.identification.key;
			if (!result[key]) {
				result[key] = [];
			}
			result[key].push(app);
			return result;
		},
		<{ [key: string]: AppModelWithManifest[] }>{}
	);

	return Object.values(appMap).map(appEntries => {
		appEntries.sort((a, b) => a.manifest.identification.version - b.manifest.identification.version);

		const [firstAppmodelWithManifest, ...remainingAppModelsWithManifest] = appEntries;

		const compositeAppModel = remainingAppModelsWithManifest.reduce(
			({ model: previousModel }, { model: currentModel, manifest }) => {
				return { model: addApplicationModel(currentModel, previousModel, manifest, true), manifest };
			},
			firstAppmodelWithManifest
		);

		return isStammdatenAppModel(compositeAppModel)
			? addStammdatenOverviewScene(compositeAppModel)
			: addOverviewScene(compositeAppModel);
	});
}

function isStammdatenAppModel(appmodel: AppModelWithManifest): boolean {
	return appmodel.model.header.id.endsWith("_S_appmodel");
}

/**
 * Add a scene and menu entry for the crossversion overview model to the app model.
 * @param appmodelWithManifest The crossversion app model, which already contains modules from each version as well as
 * the manifest of the newest version, which is considered the definitive manifeset
 */
function addOverviewScene(appmodelWithManifest: AppModelWithManifest): AppModelWithManifest {
	const {
		model: compositeAppModel,
		manifest: {
			identification: { shortName: appShortName, id: appId, key: appKey },
			models: { mainDocumentModelId }
		}
	} = appmodelWithManifest;
	const { manifest } = appmodelWithManifest;

	const appModel = cloneDeep(compositeAppModel);
	let appModule: MutableApplicationModule | undefined = appModel.content.modules.find(
		it => it.name === `${appId}_Module`
	);

	if (!appModule) {
		throw new Error("No app module found in Application  " + appId);
	}

	const processId = appModel.header.annotations?.find(it => it.name === APPMODEL_PROCESS_ID_ANNOTATION)?.value;
	const initialTaskForm = appModel.header.annotations?.find(
		it => it.name === APPMODEL_INITIAL_TASK_FORM_ANNOTATION
	)?.value;

	if (!processId || !initialTaskForm) {
		throw new Error(`Appmodel for Fachverfahren ${appId} must include processId and initialTaskForm annotations`);
	}

	if (appModule.flows.length !== 1) {
		throw new Error(
			`Fachverfahren appmodels must currently have exactly one flow, but Fachverfahren ${appId} has ${appModule.flows.length}`
		);
	}

	appModule = {
		...appModule,
		menu: {
			name: "Open approvals",
			label: [
				{
					locale: "de",
					text: appShortName
				}
			],
			initialActivity: {
				descriptor: {
					model: mainDocumentModelId,
					processes: processId,
					fachverfahren: appId,
					view: WorkflowsConstants.TASK_LIST_VIEW
				}
			},
			permission: ""
		},
		flows: [
			{
				...appModule.flows[0],
				scenes: [
					...appModule.flows[0].scenes,
					{
						name: "Overview",
						matchConditions: [
							{
								key: "model",
								mustEqual: mainDocumentModelId
							},
							{
								key: "processes",
								mustEqual: processId
							},
							{
								key: "instance",
								isSet: false
							}
						],
						sceneChange: {
							onEnter: [
								{
									type: "REGION_CLEAR"
								},
								{
									type: "VIEW_ADD",
									name: "ProcessOverviewEngine",
									models: [
										{
											modelType: "overview",
											name: `${appKey}_${CROSSVERSION_OVERVIEW_MODEL_NAME}`
										},
										{
											modelType: "document",
											name: `${appKey}_${CROSSVERSION_DOC_MODEL_NAME}`
										}
									],
									configuration: {
										initialTaskForm
									}
								}
							]
						}
					}
				]
			}
		]
	};
	return {
		model: {
			...appModel,
			content: {
				...appModel.content,
				modules: [
					...appModel.content.modules.filter(it => it.name !== `${appId}_${FV_MAIN_MODULE_NAME}`),
					appModule
				]
			}
		},
		manifest
	};
}

function addStammdatenOverviewScene(appmodelWithManifest: AppModelWithManifest): AppModelWithManifest {
	const {
		model: compositeAppModel,
		manifest: {
			identification: { shortName: appShortName, id: appId, key: appKey },
			models: { mainDocumentModelId }
		}
	} = appmodelWithManifest;
	const { manifest } = appmodelWithManifest;

	const appModel = cloneDeep(compositeAppModel);
	let appModule: MutableApplicationModule | undefined = appModel.content.modules.find(
		it => it.name === `Stammdaten_Module`
	);

	if (!appModule) {
		throw new Error("No app module found in Application  " + appId);
	}

	if (appModule.flows.length === 0) {
		throw new Error(
			`Stammdaten appmodels must currently have at least one flow, but Stammdaten ${appId} has no flows.`
		);
	}

	const latestAppFlow = appModule.flows.find(e => e.name.startsWith(appId));
	if (!latestAppFlow) {
		throw new Error(`Stammdaten appmodels must currently does not have a flow matching to ${appId}.`);
	}

	const filteredMenu = appModule.menu?.children?.filter(e => !e.name.startsWith(appKey) || e.name.startsWith(appId));

	appModule = {
		...appModule,
		menu: {
			...appModule.menu,
			name: "Open Stammdaten",
			label: [
				{
					locale: "de",
					text: appShortName
				}
			],
			permission: "",
			children: filteredMenu
		},
		flows: [
			...appModule.flows.slice(0, -1),
			{
				...latestAppFlow,
				scenes: [
					...latestAppFlow.scenes.filter(
						it => it.name !== "StammdatenOverview" && it.name !== "StammdatenForm"
					),
					{
						name: "StammdatenOverview",
						matchConditions: [
							{
								key: "model",
								mustEqual: mainDocumentModelId
							},
							{
								key: "instance",
								isSet: false
							}
						],
						sceneChange: {
							onEnter: [
								{
									layout: {
										name: "StammdatenMasterDetail"
									},
									type: "REGION_CLEAR"
								},
								{
									type: "VIEW_ADD",
									name: "StammdatenOverviewEngine",
									models: [
										{
											modelType: "overview",
											name: `${appKey}_StammdatenBasis_S_O`
										},
										{
											modelType: "document",
											name: `${appKey}_StammdatenBasis_S_D`
										}
									]
								}
							]
						}
					},
					{
						name: "StammdatenForm",
						matchConditions: [
							{
								key: "model",
								mustEqual: mainDocumentModelId
							},
							{
								key: "instance",
								isSet: true
							}
						],
						sceneChange: {
							onEnter: [
								{
									type: "VIEW_ADD",
									name: "StammdatenFormEngine",
									models: [
										{
											modelType: "form",
											name: `${appId}_StammdatenBasis_S_F`
										},
										{
											modelType: "document",
											name: `${appId}_StammdatenBasis_S_D`
										}
									]
								}
							]
						}
					},
					{
						name: "Changelog",
						matchConditions: [
							{
								key: "model",
								mustEqual: CHANGELOG_DOCUMENT_MODEL
							}
						],
						sceneChange: {
							onEnter: [
								{
									type: "VIEW_ADD",
									name: "ChangelogFormEngine",
									models: [
										{
											modelType: "document",
											name: CHANGELOG_DOCUMENT_MODEL
										},
										{
											modelType: "form",
											name: "Changelog_F",
											documentModel: "Changelog_D"
										}
									]
								}
							]
						}
					}
				]
			}
		]
	};

	return {
		model: {
			...appModel,
			content: {
				...appModel.content,
				modules: [...appModel.content.modules.filter(it => it.menu?.name !== "Stammdaten"), appModule]
			}
		},
		manifest
	};
}
